/**
 * useNotifications — polls the proxy server for recent pod-member collection additions.
 *
 * Tracks a per-appType "last read" timestamp in localStorage so the red dot
 * only appears for notifications the user hasn't seen yet.
 *
 * @param {import('../services/NotificationService').default | null} notificationService
 * @param {'books'|'movies'|'games'|'boardgames'} appType
 * @returns {{ notifications: Array, unreadCount: number, markAllRead: () => void, loading: boolean, refresh: () => void }}
 */

import { useState, useEffect, useCallback, useRef } from 'react';

const BASE_POLL_MS = 2 * 60 * 1000; // 2 minutes
const MAX_POLL_MS = 16 * 60 * 1000; // 16 minutes max backoff

export default function useNotifications(notificationService, appType) {
    const [notifications, setNotifications] = useState([]);
    const [loading, setLoading] = useState(false);
    const failCountRef = useRef(0);
    const intervalRef = useRef(null);

    const storageKey = `notifications-last-read-${appType}`;
    const [lastReadAt, setLastReadAt] = useState(
        () => localStorage.getItem(storageKey) || new Date(0).toISOString(),
    );

    const scheduleNext = useCallback(() => {
        clearInterval(intervalRef.current);
        const delay = Math.min(BASE_POLL_MS * Math.pow(2, failCountRef.current), MAX_POLL_MS);
        intervalRef.current = setInterval(() => fetchNotificationsInner(), delay);
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    const fetchNotificationsInner = useCallback(async () => {
        if (!notificationService) return;
        setLoading(true);
        try {
            const data = await notificationService.fetch(appType);
            setNotifications(data.notifications || []);
            if (failCountRef.current > 0) {
                failCountRef.current = 0;
                scheduleNext(); // Reset to normal interval on recovery
            }
        } catch (e) {
            failCountRef.current = Math.min(failCountRef.current + 1, 4);
            if (failCountRef.current === 1) {
                console.warn('useNotifications fetch error:', e.message);
            }
            scheduleNext(); // Backoff interval
        } finally {
            setLoading(false);
        }
    }, [notificationService, appType, scheduleNext]);

    // Expose a stable reference for external callers
    const fetchNotifications = fetchNotificationsInner;

    // Initial fetch + polling with backoff
    useEffect(() => {
        failCountRef.current = 0;
        fetchNotificationsInner();
        intervalRef.current = setInterval(fetchNotificationsInner, BASE_POLL_MS);
        return () => clearInterval(intervalRef.current);
    }, [fetchNotificationsInner]);

    // Reset on page visibility change (tab becomes active)
    useEffect(() => {
        const handleVisibility = () => {
            if (document.visibilityState === 'visible' && failCountRef.current > 0) {
                failCountRef.current = 0;
                fetchNotificationsInner();
                scheduleNext();
            }
        };
        document.addEventListener('visibilitychange', handleVisibility);
        return () => document.removeEventListener('visibilitychange', handleVisibility);
    }, [fetchNotificationsInner, scheduleNext]);

    // Count notifications newer than the last-read timestamp
    const unreadCount = notifications.filter((n) => n.created > lastReadAt).length;

    const markAllRead = useCallback(() => {
        const now = new Date().toISOString();
        localStorage.setItem(storageKey, now);
        setLastReadAt(now);
    }, [storageKey]);

    return { notifications, unreadCount, markAllRead, loading, refresh: fetchNotifications };
}
